import { useEffect, useState } from 'react';
import { Receipt, Upload, PieChart, Shield } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { Auth } from './components/Auth';
import { ExpenseReport } from './components/ExpenseReport';
import { supabase } from './lib/supabase';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

interface Profile {
  first_name: string;
  last_name: string;
  phone_number: string;
}

function App() {
  const [session, setSession] = useState<{ user: User } | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach(event => window.addEventListener(event, updateActivity));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, []);

  // Check for inactivity
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        supabase?.auth.signOut();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [session, lastActivity]);

  useEffect(() => {
    if (session?.user) {
      fetchProfile(session.user.id);
    }
  }, [session]);

  async function fetchProfile(userId: string) {
    try {
      const { data, error } = await supabase
        ?.from('profiles')
        .select('first_name, last_name, phone_number')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }

  if (!supabase) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
          <Shield className="h-12 w-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
            Supabase Connection Required
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Please add your Supabase credentials to the .env file to enable the application.
          </p>
          <div className="bg-amber-50 p-4 rounded-md">
            <p className="text-sm text-amber-700 font-medium mb-2">Required Environment Variables:</p>
            <code className="block text-sm bg-amber-100 p-2 rounded">
              VITE_SUPABASE_URL=your-project-url<br />
              VITE_SUPABASE_ANON_KEY=your-anon-key
            </code>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    supabase?.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase?.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    }) || { data: { subscription: { unsubscribe: () => {} } } };

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {!session ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
              Receipt AI Assistant
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Automate your expense tracking with AI-powered receipt scanning.
              Upload receipts and let our assistant handle the rest.
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="flex justify-center">
                  <Upload className="h-12 w-12 text-indigo-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Easy Upload
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Simply upload your receipts and our AI will do the rest
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center">
                  <Receipt className="h-12 w-12 text-indigo-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Smart Processing
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Advanced AI extracts and categorizes expense information
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center">
                  <PieChart className="h-12 w-12 text-indigo-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Instant Reports
                </h3>
                <p className="mt-2 text-base text-gray-500">
                  Access organized expense reports in real-time
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center justify-center mb-8">
                  <Shield className="h-8 w-8 text-indigo-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Secure Login
                  </h2>
                </div>
                <Auth />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Your Expense Dashboard
              </h1>
              {profile?.first_name && (
                <span className="text-gray-600">
                  Welcome, {profile.first_name}!
                </span>
              )}
            </div>
            <button
              onClick={() => supabase?.auth.signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Sign Out
            </button>
          </div>
          <ExpenseReport />
        </div>
      )}
    </div>
  );
}

export default App;